import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { Disposable } from '../../hooks/useConstant';

import type { DataCompartment, IAppStorage } from '@aesop-fables/scrinium';
import { ScriniumServices } from '@aesop-fables/scrinium';
import { inject, createServiceModule, Scopes } from '@aesop-fables/containr';

import {
  OnboardingStepsDefault,
  OnboardingStepsAdp,
  StepData,
  OnboardingStepsMultipleProductTypes,
  OrgSignUpStateData,
  TemplateData,
} from '../../models/SignUpData';
import { SignUpCompartments, signUpStorageKey } from '../../data/signUp';
import { Location } from 'react-router';
import { ApiKeys } from '../../api/apis/ApiKeys';
import { GooglePlacesApi } from '../../api/apis/GooglePlacesApi';
import { PricebookCompartments, pricebookStorageKey } from '../../data/pricebook';
import { PricebookBundle, ProductTypeEnum } from '../../models/PricebookData';
import { OrgSignUpStateApi } from '../../api/apis/OrgSignUpStateApi';
export const signUpWizardKey = 'services/signUpWizard';

export enum OnboardingTemplateIdEnum {
  'Default' = 1,
  'Adp' = 2,
}

const getOnboardingSteps = (
  template: TemplateData,
  multipleProductTypes: boolean = false,
): StepData[] => {
  switch (template?.id) {
    case OnboardingTemplateIdEnum.Adp:
      return OnboardingStepsAdp;
    default:
      if (multipleProductTypes) {
        return OnboardingStepsMultipleProductTypes;
      }
      return OnboardingStepsDefault;
  }
};

class SignUpWizard implements Disposable {
  private readonly signUpComplete: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private templateId = new BehaviorSubject<OnboardingTemplateIdEnum>(
    OnboardingTemplateIdEnum.Default,
  );
  private pricebookOptions = new BehaviorSubject<PricebookBundle[] | undefined>(undefined);
  private multipleProductTypes = new BehaviorSubject<boolean>(false);
  private multiplePricebooks = new BehaviorSubject<boolean>(false);
  private onboardingSteps: StepData[] = OnboardingStepsDefault;
  private googleKey: BehaviorSubject<string> = new BehaviorSubject('');
  private signUpResumed = new BehaviorSubject<boolean>(false);

  constructor(
    @inject(ScriniumServices.AppStorage)
    private readonly appStorage: IAppStorage,
    @inject(ApiKeys.OrgSignUpState)
    private readonly orgSignUpStateApi: OrgSignUpStateApi,
    @inject(ApiKeys.GooglePlaces)
    private readonly googlePlacesApi: GooglePlacesApi,
  ) {
    const signUpDataCache = this.appStorage.retrieve<SignUpCompartments>(signUpStorageKey);
    const pricebookDataCache = this.appStorage.retrieve<PricebookCompartments>(pricebookStorageKey);

    combineLatest([
      signUpDataCache.observe$<TemplateData>('onboardingTemplate'),
      pricebookDataCache.observe$<PricebookBundle[]>('pricebookOptions'),
    ]).subscribe(() => {
      this.init();
    });
  }

  async init(forceReload = false) {
    try {
      const signUpDataCache = this.appStorage.retrieve<SignUpCompartments>(signUpStorageKey);
      const pricebookDataCache =
        this.appStorage.retrieve<PricebookCompartments>(pricebookStorageKey);

      const template = (
        signUpDataCache.findCompartment('onboardingTemplate') as DataCompartment<TemplateData>
      ).getData() as TemplateData;

      let pricebookOptions = (
        pricebookDataCache.findCompartment('pricebookOptions') as DataCompartment<PricebookBundle[]>
      ).getData() as PricebookBundle[];

      if (!pricebookOptions && forceReload) {
        await pricebookDataCache.reload('pricebookOptions');
        pricebookOptions = (
          pricebookDataCache.findCompartment('pricebookOptions') as DataCompartment<
            PricebookBundle[]
          >
        ).getData() as PricebookBundle[];
      }

      const basePricebookOptions = pricebookOptions?.filter(option => !option.pricebook.addOn);
      const nickelsProducts = basePricebookOptions?.some(p =>
        p.pricebook.productType.includes(ProductTypeEnum.Nickels),
      );
      const gcProducts = basePricebookOptions?.some(
        p => p.pricebook.productType === ProductTypeEnum.GuidedChoice,
      );
      const multipleProductTypes = nickelsProducts && gcProducts;

      this.templateId.next(template?.id ?? OnboardingTemplateIdEnum.Default);
      this.pricebookOptions.next(basePricebookOptions);
      this.multiplePricebooks.next(basePricebookOptions?.length > 1);
      this.multipleProductTypes.next(multipleProductTypes);
      this.onboardingSteps = getOnboardingSteps(template, multipleProductTypes);

      const googleKey = await this.googlePlacesApi.get();
      this.googleKey.next(googleKey.data);
    } catch (err) {
      console.error(err);
    }
  }

  get signUpComplete$(): Observable<boolean> {
    return this.signUpComplete;
  }

  get googlePlacesKey$(): Observable<string> {
    return this.googleKey;
  }

  get multipleProductTypes$(): Observable<boolean> {
    return this.multipleProductTypes;
  }

  get multiplePricebooks$(): Observable<boolean> {
    return this.multiplePricebooks;
  }

  get signUpResumed$(): Observable<boolean> {
    return this.signUpResumed;
  }

  setSignUpResumed(val: boolean): void {
    this.signUpResumed.next(val);
  }

  getCurrent(location: Location): string {
    return location.pathname.split('onboarding/')[1];
  }

  resumeOnboarding(lastCompletedStep: string): StepData {
    const index = this.onboardingSteps.findIndex(s => s.current === lastCompletedStep);
    const step = this.onboardingSteps.at(index + 1);
    return (
      step ??
      this.onboardingSteps.at(0) ?? {
        current: 'aboutYourOrg',
        route: '/onboarding/aboutYourOrg',
        previousCheckpoint: '',
        totalSteps: 4,
        activeStep: 1,
      }
    );
  }

  nextStep(location: Location): StepData {
    const current = location.pathname.split('onboarding/')[1];
    const index = this.onboardingSteps.findIndex(s => s.current === current);
    const step = this.onboardingSteps.at(index + 1);
    return (
      step ??
      this.onboardingSteps.at(0) ?? {
        current: 'aboutYourOrg',
        route: '/onboarding/aboutYourOrg',
        previousCheckpoint: '',
        totalSteps: 4,
        activeStep: 1,
      }
    );
  }

  currentStep(location: Location): StepData {
    const current = this.getCurrent(location);
    const step = this.onboardingSteps.find(s => s.current === current);
    return (
      step ??
      this.onboardingSteps.at(0) ?? {
        current: 'aboutYourOrg',
        route: '/onboarding/aboutYourOrg',
        previousCheckpoint: '',
        totalSteps: 4,
        activeStep: 1,
      }
    );
  }

  previousStep(location: Location): StepData {
    const current = location.pathname.split('onboarding/')[1];
    const index = this.onboardingSteps.findIndex(s => s.current === current);
    const step = this.onboardingSteps.at(index - 1);
    return (
      step ??
      this.onboardingSteps.at(0) ?? {
        current: 'aboutYourOrg',
        route: '/onboarding/aboutYourOrg',
        previousCheckpoint: '',
        totalSteps: 4,
        activeStep: 1,
      }
    );
  }

  agreeAndSignUp() {
    this.signUpComplete.next(true);
  }

  reset() {
    this.signUpComplete.next(false);
    this.signUpResumed.next(false);
  }

  dispose(): void {
    console.log('disposing SignUpWizard');
  }

  async setPricebookCount(selectedProduct: ProductTypeEnum): Promise<void> {
    const pricebookOptions = this.pricebookOptions.getValue();
    if (!pricebookOptions) return;

    const pricebooksCount = pricebookOptions.filter(p =>
      p.pricebook.productType.includes(selectedProduct),
    ).length;

    this.multiplePricebooks.next(pricebooksCount > 1);
  }

  async setProductIfOnlyOneProductType(): Promise<void> {
    const signUpDataCache = this.appStorage.retrieve<SignUpCompartments>(signUpStorageKey);
    const orgSignUpState = (
      signUpDataCache.findCompartment('orgSignUpState') as DataCompartment<OrgSignUpStateData>
    ).getData() as OrgSignUpStateData;

    const templateId = this.templateId.getValue();
    const pricebookOptions = this.pricebookOptions.getValue();

    if (
      templateId !== OnboardingTemplateIdEnum.Adp &&
      !this.onboardingSteps.some(step => step.current === 'chooseProduct')
    ) {
      if (!pricebookOptions?.length) return;

      const product =
        pricebookOptions[0].pricebook.productType === ProductTypeEnum.GuidedChoice
          ? ProductTypeEnum.GuidedChoice
          : ProductTypeEnum.Nickels;

      if (orgSignUpState?.state?.product === product) return;

      await this.orgSignUpStateApi.patch({
        state: {
          ...orgSignUpState?.state,
          product,
        },
      });
      await signUpDataCache.reloadAll();
    }
  }
}

export const withSignUpWizard = createServiceModule(signUpWizardKey, services => {
  services.autoResolve<SignUpWizard>(signUpWizardKey, SignUpWizard, Scopes.Singleton);
});

export { SignUpWizard };
